import React from "react";
import { Link } from "react-router-dom";
import UndoInline from "./UndoInline";

const STATUS_COLORS = {
  delivered: "text-emerald-700",
  capped: "text-amber-700",
  duplicate: "text-blue-700",
  error: "text-red-700",
  skipped: "text-zinc-600",
};

function CountsRow({ result, showDup, showCapped }) {
  return (
    <div className="mt-2 font-mono text-sm flex flex-wrap gap-4">
      <span>Created: <b>{result.created}</b></span>
      {showDup && <span data-testid="csv-result-duplicate">Duplicate: <b>{result.duplicate}</b></span>}
      {showCapped && <span data-testid="csv-result-capped">Capped: <b>{result.capped}</b></span>}
      <span>Skipped: <b>{result.skipped}</b></span>
    </div>
  );
}

function SampleIds({ ids }) {
  if (!ids?.length) return null;
  return (
    <div className="mt-2 text-xs font-mono">
      Sample: {ids.slice(0, 5).map((id) => (
        <Link key={id} to={`/leads/${id}`} className="mr-2 hover:underline">{id.slice(0, 8)}</Link>
      ))}
    </div>
  );
}

function PerRowTable({ rows }) {
  if (!rows?.length) return null;
  return (
    <details className="mt-3 text-xs" data-testid="csv-per-row">
      <summary className="cursor-pointer uppercase tracking-widest">
        Per-row breakdown ({rows.length} shown)
      </summary>
      <table className="mt-2 w-full font-mono text-[11px]">
        <thead>
          <tr className="border-b border-black text-[10px] uppercase tracking-widest">
            <th className="py-1 text-left">Row</th>
            <th className="py-1 text-left">Status</th>
            <th className="py-1 text-left">Lead / reason</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.row} className="border-b border-zinc-200">
              <td className="py-1 pr-2">{r.row}</td>
              <td className={`py-1 pr-2 ${STATUS_COLORS[r.status] || STATUS_COLORS.skipped}`}>
                <b>{r.status}</b>
              </td>
              <td className="py-1">
                {r.id
                  ? <Link to={`/leads/${r.id}`} className="hover:underline">{r.id.slice(0, 8)}</Link>
                  : (r.reason || "—")}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </details>
  );
}

function ErrorList({ errors }) {
  if (!errors?.length) return null;
  return (
    <details className="mt-3 text-xs">
      <summary className="cursor-pointer uppercase tracking-widest">Row errors ({errors.length})</summary>
      <ul className="mt-2 font-mono">
        {errors.map((e) => (
          <li key={`row-${e.row}`} className="border-b border-zinc-200 py-1">
            Row {e.row}: {e.error}
          </li>
        ))}
      </ul>
    </details>
  );
}

// Step 3 of the CSV wizard — post-import summary + optional deep dives.
export default function CsvResultStep({ result, onReset, onGoToLeads }) {
  const showDup = typeof result.duplicate === "number" && result.duplicate > 0;
  const showCapped = typeof result.capped === "number" && result.capped > 0;

  return (
    <div className="brut-card mt-4 p-6" data-testid="wiz-step-3">
      <div className="font-chivo font-bold uppercase tracking-tight">Result</div>
      <CountsRow result={result} showDup={showDup} showCapped={showCapped} />
      <SampleIds ids={result.ids} />
      <PerRowTable rows={result.per_row} />
      <ErrorList errors={result.errors} />

      <div className="mt-4 flex flex-wrap gap-2 items-center">
        <button onClick={onReset}
          className="border border-black px-3 py-2 text-xs uppercase tracking-widest">
          Import another CSV
        </button>
        <button onClick={onGoToLeads}
          className="bg-black text-white border border-black px-3 py-2 text-xs uppercase tracking-widest">
          Go to leads
        </button>
        {result.batch_id && result.created > 0 && (
          <UndoInline batchId={result.batch_id} />
        )}
      </div>
    </div>
  );
}
